import React from "react";
import { AlertCircle, LoaderCircle } from "lucide-react";
import EmptyState from "ui/EmptyState";
import { Button } from "ui/Button";

interface SectionLoadingStateProps {
  message: string;
}

interface SectionErrorStateProps {
  message: string;
  retryLabel: string;
  onRetry: () => void;
  isRetrying?: boolean;
}

interface SectionEmptyStateProps {
  title: string;
  subtitle?: string;
  cta?: React.ReactNode;
}

/**
 * Loading placeholder shared by dashboard sections.
 */
export const SectionLoadingState: React.FC<SectionLoadingStateProps> = ({ message }) => (
  <div
    role="status"
    className="flex min-h-[10rem] flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-eoe-border/70 bg-eoe-bg-hover/40 px-4 py-8 text-center dark:border-eoe-dark-border/70 dark:bg-eoe-dark-bg-hover/30"
  >
    <LoaderCircle className="size-5 animate-spin text-eoe-text-sub dark:text-eoe-dark-text-sub" aria-hidden />
    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{message}</p>
  </div>
);

/**
 * Error state shared by dashboard sections.
 */
export const SectionErrorState: React.FC<SectionErrorStateProps> = ({
  message,
  retryLabel,
  onRetry,
  isRetrying = false,
}) => (
  <div className="flex min-h-[10rem] flex-col items-center justify-center gap-4 rounded-2xl border border-red-300/60 bg-red-50/70 px-4 py-8 text-center dark:border-red-500/40 dark:bg-red-950/20">
    <AlertCircle className="size-5 text-red-600 dark:text-red-400" aria-hidden />
    <p className="max-w-xl text-sm text-red-700 dark:text-red-300">{message}</p>
    <Button
      type="button"
      variant="outline"
      onClick={onRetry}
      disabled={isRetrying}
      aria-label={retryLabel}
      className="border-red-300/80 bg-white/70 text-red-700 hover:bg-white dark:border-red-500/40 dark:bg-red-950/20 dark:text-red-200 dark:hover:bg-red-950/30"
    >
      {retryLabel}
    </Button>
  </div>
);

/**
 * Empty state shared by dashboard sections.
 */
export const SectionEmptyState: React.FC<SectionEmptyStateProps> = ({ title, subtitle, cta }) => (
  <EmptyState
    title={title}
    subtitle={subtitle}
    cta={cta}
    className="min-h-[10rem] justify-center rounded-2xl border border-dashed border-eoe-border/70 bg-eoe-bg-hover/40 dark:border-eoe-dark-border/70 dark:bg-eoe-dark-bg-hover/30"
  />
);

